// EVA-289: the Exit Price Assumptions tab hydrates from two sources whose key
// casing differs, so the mapping lives here as a pure function rather than
// inline in the component — this is what makes the tab unit-testable.
//
//   ERB props   -> the mount controller runs them through humps `camelizeKeys`,
//                  which recurses into arrays: `input_method` -> `inputMethod`.
//                  The ERB embeds the assumptions and the schedule, but not the
//                  computed half — that arrives only from the two sources below.
//   fetch/cable -> raw JSON straight off the wire, still snake_case.
//
// Reading only snake_case silently mis-hydrates every persisted row on page
// load: a Fixed Amount row would render as "% of Net Sale Price" and the next
// save would rewrite 250,000 currency units as 250000 percent. Reading only
// camelCase breaks the save/broadcast round-trip instead. Both are accepted.

export const ANALYSIS_END = 'ANALYSIS_END'
export const SPECIFIED_DATE = 'SPECIFIED_DATE'
export const TWELVE_MONTHS_AFTER_SALE = 'TWELVE_MONTHS_AFTER_SALE'
export const YEAR_OF_SALE = 'YEAR_OF_SALE'
export const MANUAL = 'MANUAL'

export const PURCHASERS_COSTS = 'PURCHASERS_COSTS'
export const VENDORS_COSTS = 'VENDORS_COSTS'
export const FIXED_AMOUNT = 'FIXED_AMOUNT'
export const PERCENT_OF_NET_SALE_PRICE = 'PERCENT_OF_NET_SALE_PRICE'
export const PERCENT_OF_ADJUSTED_GROSS_SALE_PRICE = 'PERCENT_OF_ADJUSTED_GROSS_SALE_PRICE'

export const PERCENT_INPUT_METHODS = [
  PERCENT_OF_NET_SALE_PRICE,
  PERCENT_OF_ADJUSTED_GROSS_SALE_PRICE
]

// The five capital deductions, in the order the developer note fixes for the
// bridge. Selection order must not change it.
// snake_case deliberately: these are the same keys the serializer puts on the
// bridge's capital_deduction_lines and the same ones the locale file uses, so
// nothing has to re-case them at the point of use.
export const CAPITAL_DEDUCTION_KEYS = [
  'void_deduction',
  'rent_free_deduction',
  'tenant_improvements',
  'leasing_commissions',
  'capital_expenses'
]

const CAPITAL_DEDUCTION_PROPS = {
  void_deduction: ['deductVoidDeduction', 'deduct_void_deduction'],
  rent_free_deduction: ['deductRentFreeDeduction', 'deduct_rent_free_deduction'],
  tenant_improvements: ['deductTenantImprovements', 'deduct_tenant_improvements'],
  leasing_commissions: ['deductLeasingCommissions', 'deduct_leasing_commissions'],
  capital_expenses: ['deductCapitalExpenses', 'deduct_capital_expenses']
}

// `??` rather than `||` throughout: an explicit false (a cleared checkbox) and
// an explicit "0" (a rate the valuer typed) are real values and must not fall
// through to the default.
const pick = (source, camel, snake) => source[camel] ?? source[snake]

export function normalizeRow(row) {
  const amount = row.amount

  return {
    id: row.id ?? null,
    name: row.name ?? '',
    deductionType: pick(row, 'deductionType', 'deduction_type') ?? PURCHASERS_COSTS,
    inputMethod: pick(row, 'inputMethod', 'input_method') ?? PERCENT_OF_NET_SALE_PRICE,
    // Distinguishes a blank amount from an explicit zero, all the way down to a
    // nullable column.
    amount: amount === null || amount === undefined || amount === '' ? null : amount
  }
}

export function normalizeProps(props) {
  const capitalDeductions = {}
  CAPITAL_DEDUCTION_KEYS.forEach((key) => {
    const [camel, snake] = CAPITAL_DEDUCTION_PROPS[key]
    capitalDeductions[key] = Boolean(pick(props, camel, snake) ?? false)
  })

  return {
    exitRatePercent: pick(props, 'exitRatePercent', 'exit_rate_percent') ?? null,
    exitDateBasis: pick(props, 'exitDateBasis', 'exit_date_basis') ?? ANALYSIS_END,
    specifiedExitDate: pick(props, 'specifiedExitDate', 'specified_exit_date') ?? null,
    noiCalculationMethod:
      pick(props, 'noiCalculationMethod', 'noi_calculation_method') ?? TWELVE_MONTHS_AFTER_SALE,
    manualNoiToCapitalise:
      pick(props, 'manualNoiToCapitalise', 'manual_noi_to_capitalise') ?? null,
    addBackVoidDeduction:
      Boolean(pick(props, 'addBackVoidDeduction', 'add_back_void_deduction') ?? false),
    addBackRentFreeDeduction:
      Boolean(pick(props, 'addBackRentFreeDeduction', 'add_back_rent_free_deduction') ?? false),
    capitalDeductions,
    rows: (pick(props, 'saleDeductions', 'sale_deductions') ?? []).map(normalizeRow)
  }
}

// The computed half of the payload. It is never edited, only displayed, so it is
// kept out of the form state entirely — mixing it in would make every autosave
// response look like a change to the user's own inputs.
export function normalizeComputed(props) {
  return {
    resolvedExitDate: pick(props, 'resolvedExitDate', 'resolved_exit_date') ?? null,
    discountRatePercent: pick(props, 'discountRatePercent', 'discount_rate_percent') ?? null,
    noiMetrics: pick(props, 'noiMetrics', 'noi_metrics') ?? {},
    bridge: props.bridge ?? {},
    rentApportionment: pick(props, 'rentApportionment', 'rent_apportionment') ?? { rows: [] },
    analytics: props.analytics ?? {}
  }
}

// Local keys for rows the server has not seen yet. React needs a stable key per
// row, and a brand new row has no id to build one from.
let localRowSeq = 0
export const nextLocalRowKey = () => `new-${(localRowSeq += 1)}`

// One definition of "a row the user has not filled in yet", shared by the tab's
// Add button and by the seed below — they must stay the same row, or the seeded
// one behaves differently from an added one on save.
export function blankDraftRow() {
  return {
    key: nextLocalRowKey(),
    id: null,
    name: '',
    deductionType: PURCHASERS_COSTS,
    inputMethod: PERCENT_OF_NET_SALE_PRICE,
    amount: ''
  }
}

// An empty schedule presents one blank row rather than the empty-state message,
// so entering a cost takes typing and not a click first — the same affordance
// EVA-284 added to the Purchasers' Costs table, and for the same reason.
//
// The seed belongs to *opening* the tab, so only the two hydration paths that
// make up one page load take it: the mount initialiser and the state re-read.
// The cable handler deliberately does not — a broadcast is the server's truth
// arriving mid-session, and seeding there would answer another tab's deletion of
// the last row by growing a fresh blank one, which reads as the deletion undoing
// itself.
//
// `previousRows` is what keeps the state re-read from replacing the row the
// mount just put on screen. Both produce an identical blank row, but a new one
// carries a new key, and a new key remounts the <tr> — dropping the caret of a
// user who has clicked into the field but not yet typed, which is exactly the
// window in which the re-read is still allowed through.
//
// A read-only viewer gets no seed: they cannot type, so a disabled blank row
// would read as a cost line whose name is missing.
export function withSeededRow(state, { readOnly, previousRows = [] }) {
  if (readOnly || state.rows.length > 0) return state

  const [candidate] = previousRows
  const reusable = previousRows.length === 1 && (candidate.id === null || candidate.id === undefined)

  return { ...state, rows: [reusable ? candidate : blankDraftRow()] }
}
